import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import type { DeliveryRecordViewModel, DeliveryStatus } from '@/features/patient-record/types';
import { formatDate } from '@/lib/format';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

function statusVariant(status: DeliveryStatus): BadgeVariant {
  switch (status) {
    case 'DELIVERED':
      return 'default';
    case 'SENT':
      return 'secondary';
    case 'FAILED':
    case 'BOUNCED':
    case 'COMPLAINED':
      return 'destructive';
    case 'QUEUED':
    case 'SUPPRESSED':
    default:
      return 'outline';
  }
}

/** Compact badge for a single delivery state, shown next to the questionnaire status on the row. */
export function DeliveryStatusBadge({ status }: { readonly status: DeliveryStatus }): ReactElement {
  const { t } = useTranslation();
  return (
    <Badge
      variant={statusVariant(status)}
      className="h-5 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wide shrink-0"
    >
      {t(`patient.questionnaires.deliveryStatus.${status}`)}
    </Badge>
  );
}

/** The full delivery attempt history (newest first) shown in a row's expanded disclosure. */
export function QuestionnaireDeliveryHistory({
  deliveries,
}: {
  readonly deliveries: readonly DeliveryRecordViewModel[];
}): ReactElement {
  const { t } = useTranslation();

  if (deliveries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('patient.questionnaires.deliveries.empty')}</p>
    );
  }

  return (
    <ul className="space-y-1.5">
      {deliveries.map((delivery) => {
        const when = delivery.statusUpdatedAt ?? delivery.sentAt ?? delivery.requestedAt;
        return (
          <li key={delivery.id} className="flex flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-2">
              <DeliveryStatusBadge status={delivery.status} />
              {delivery.attemptNo !== null && (
                <span className="text-[11px] text-muted-foreground">
                  {t('patient.questionnaires.deliveries.attempt', { n: delivery.attemptNo })}
                </span>
              )}
              {when !== null && (
                <span className="text-[11px] text-muted-foreground">{formatDate(when)}</span>
              )}
            </span>
            {delivery.recipient !== null && (
              <span className="text-[11px] text-muted-foreground truncate">{delivery.recipient}</span>
            )}
            {delivery.errorMessage !== null && (
              <span className="text-[11px] text-destructive">{delivery.errorMessage}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
